/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React, { useEffect, useState } from "react";
import PromiseLadeEigeneMandantRollen, { Rollen } from "../../../Management/Mandanten/Queries/PromiseLadeEigeneMandantRollen";
import { useAuthenticationContext } from "../../Authentication/AuthenticationContext";
import { useIstMandantGueltigContext } from "../../Authentication/IstMandantGueltigContext";
import { environment } from "../../environment";
import StringUtil from "../../Utils/StringUtil";

export interface IAppRollenContext extends AppRollenContextState {
    aktualisiere: () => Promise<void>;
}

interface AppRollenContextState {
    istEigentuemer: boolean;
    istAdministration: boolean;
    istSupport: boolean;
    istExperimentell: boolean;
    istNutzend: boolean;
    istTeilnehmend: boolean;
    istDozierend: boolean;
    istEigentuemerOderSupport: boolean;
    loading: boolean;
}


function createAppRollenContextDefaultValue(): IAppRollenContext {
    return {
        istAdministration: false,
        istSupport: false,
        istExperimentell: false,
        istEigentuemer: false,
        istNutzend: false,
        istTeilnehmend: false,
        istDozierend: false,
        istEigentuemerOderSupport: false,
        loading: true,
        aktualisiere: () => Promise.resolve()
    };
}

const appRollenContext = React.createContext<IAppRollenContext>(createAppRollenContextDefaultValue());

export default function AppRollenContext(props: { children: React.ReactNode }) {
    const [state, setState] = useState<AppRollenContextState>(createAppRollenContextDefaultValue());
    const { isLoggedIn, isInitialized } = useAuthenticationContext() ?? {}
    const istMandantGueltig = useIstMandantGueltigContext() ?? false;
    const kannEigeneMandantRollenLaden = !!isInitialized && !!isLoggedIn && istMandantGueltig;
    const query = new PromiseLadeEigeneMandantRollen();

    const aktualisiere = () => query.execute({ mandantKennungOderUrlBezeichnung: environment.mandant }).then(
        (rollen: Rollen) => {
            setState({ ...rollen, istEigentuemerOderSupport: rollen.istSupport || rollen.istEigentuemer, loading: false });
        }
    );

    useEffect(() => {
        if (kannEigeneMandantRollenLaden) {
            if (!StringUtil.isEmpty(environment.mandant) && state.loading) {
                aktualisiere();
            }
        } else {
            setState(createAppRollenContextDefaultValue());
        }
    }, [kannEigeneMandantRollenLaden, state.loading]);

    return (
        <appRollenContext.Provider value={{ ...state, aktualisiere }}>
            {props.children}
        </appRollenContext.Provider>
    );
}

export function useAppRollenContext(): IAppRollenContext {
    return React.useContext(appRollenContext) ?? createAppRollenContextDefaultValue();
}